import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { updatePriorityStatuses } from '..';
import {
  selectAdvisorHasClients,
  selectAdvisorOnboardingCompletedStepsData,
  selectPriorityStatusByEngagement,
  selectSessionAdvisor,
  selectShouldDisallowUsage,
} from '../model/selectors';

import { AdvisorAccountActionRequired } from '@client/pages/AdvisorAccountActionRequired';
import { AdvisorAccountSponsorship } from '@client/pages/AdvisorAccountSponsorship';
import { AdvisorAccountSeats } from '@client/pages/AdvisorAccountSeats';
import { AdvisorIntegrations } from '@client/pages/AdvisorIntegrations';
import { AdvisorWelcome } from '@client/pages/AdvisorWelcome';
import { Menu } from 'lucide-react';
import { AdvisorDashboardClients } from '../pages/AdvisorDashboardClients';
import {
  AdvisorDashboardOpportunities,
  useFetchQualifyingClients,
} from '../pages/AdvisorDashboardOpportunities';
import { AdvisorDashboardSettings } from '../pages/AdvisorDashboardSettings';
import { useAdvisorNavigation } from './AdvisorDashboardRouteGuard';
import { ONBOARDING_STEPS } from '@client/hooks/useAdvisorOnboarding';
import {
  AdvisorDashboardSideBar,
  AdvisorDashboardSideBarContent,
  AdvisorDashboardSideBarMobile,
} from './AdvisorDashboardSideBar';
import { useSignOut } from './hooks/use-sign-out';
import { Button } from './ui/button';
import { InviteClientModal } from '@/components/invite-client/InviteClientModal';

export type DashboardActivePage =
  | 'clients'
  | 'settings'
  | 'sponsorship'
  | 'seats'
  | 'integrations' /* | 'opportunities' */;

// Custom hooks for AdvisorDashboardShell
const useActivePage = () => {
  const location = useLocation();

  const getActivePageFromPath = (): DashboardActivePage => {
    const path = location.pathname;
    const normalizedPath = path.replace(/\/advisors\/[^/]+/, '/advisors');

    if (normalizedPath.includes('/settings')) return 'settings';
    // else if (normalizedPath.includes('/opportunities')) return 'opportunities';
    else if (normalizedPath.includes('/seats')) return 'seats';
    else if (normalizedPath.includes('/sponsorship')) return 'sponsorship';
    else if (normalizedPath.includes('/integrations')) return 'integrations';
    else return 'clients';
  };

  const [activePage, setActivePage] = useState<DashboardActivePage>(getActivePageFromPath());

  useEffect(() => {
    const newActivePage = getActivePageFromPath();
    setActivePage(newActivePage);
  }, [location.pathname]);

  return { activePage, getActivePageFromPath };
};

const useUnsavedSettings = () => {
  const [hasUnsavedSettings, setHasUnsavedSettings] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasUnsavedSettings && pendingNavigationPath) {
      navigate(pendingNavigationPath);
      setPendingNavigationPath(null);
    }
  }, [hasUnsavedSettings, pendingNavigationPath, navigate]);

  return {
    hasUnsavedSettings,
    setHasUnsavedSettings,
    showUnsavedDialog,
    setShowUnsavedDialog,
    pendingNavigationPath,
    setPendingNavigationPath,
  };
};

const useSidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  return { sidebarOpen, setSidebarOpen };
};

export default function AdvisorDashboardShell() {
  const advisor = useSelector(selectSessionAdvisor);
  const shouldDisallowUsage = useSelector(selectShouldDisallowUsage);
  const { organizationDisplayName } = advisor;
  const dispatch = useDispatch();
  const hasClients = useSelector(selectAdvisorHasClients);
  const priorityStatusByEngagement = useSelector(selectPriorityStatusByEngagement);
  const onboardingCompletedSteps = useSelector(selectAdvisorOnboardingCompletedStepsData);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const hasCompletedAllOnboardingSteps =
    onboardingCompletedSteps?.completedSteps &&
    ONBOARDING_STEPS.every(step => onboardingCompletedSteps.completedSteps.includes(step));
  const hasIncompleteOnboarding = !hasCompletedAllOnboardingSteps;

  const { activePage } = useActivePage();
  const handleClickSignOut = useSignOut();
  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const { hasUnsavedSettings, setHasUnsavedSettings, showUnsavedDialog, setShowUnsavedDialog } =
    useUnsavedSettings();

  const {
    navigateToClientsDashboard,
    navigateToWelcomeDashboard,
    navigateToAdvisorProfile,
    // navigateToOpportunitiesDashboard,
    navigateToSponsorshipDashboard,
    navigateToOnboarding,
    navigateToSeatsDashboard,
    navigateToIntegrations,
  } = useAdvisorNavigation();

  useEffect(() => {
    dispatch(updatePriorityStatuses(priorityStatusByEngagement));
  }, [dispatch, priorityStatusByEngagement]);

  useFetchQualifyingClients();

  const handleOnPageChange = useCallback(
    (page: DashboardActivePage) => {
      setSidebarOpen(false);
      if (hasUnsavedSettings) {
        setShowUnsavedDialog(true);
        return;
      }

      if (page === 'clients') {
        hasClients ? navigateToClientsDashboard() : navigateToWelcomeDashboard();
      } else if (page === 'settings') {
        navigateToAdvisorProfile();
      } else if (page === 'seats') {
        navigateToSeatsDashboard();
      } else if (page === 'sponsorship') {
        navigateToSponsorshipDashboard();
      } else if (page === 'integrations') {
        navigateToIntegrations();
      } /* else if (page === 'opportunities') {
        navigateToOpportunitiesDashboard();
      } */
    },
    [
      setSidebarOpen,
      hasUnsavedSettings,
      setShowUnsavedDialog,
      hasClients,
      navigateToClientsDashboard,
      navigateToWelcomeDashboard,
      navigateToAdvisorProfile,
      // navigateToOpportunitiesDashboard,
      navigateToSponsorshipDashboard,
      navigateToSeatsDashboard,
      navigateToIntegrations,
    ],
  );

  const renderAdvisorSideBarMobile = useCallback(
    () => (
      <AdvisorDashboardSideBarMobile
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      >
        <AdvisorDashboardSideBarContent
          activePage={activePage}
          onPageChange={handleOnPageChange}
          onNavigateToOnboarding={navigateToOnboarding}
          hasIncompleteOnboarding={hasIncompleteOnboarding}
          isDisabled={shouldDisallowUsage}
          handleClickSignOut={handleClickSignOut}
        />
      </AdvisorDashboardSideBarMobile>
    ),
    [
      sidebarOpen,
      setSidebarOpen,
      activePage,
      handleOnPageChange,
      shouldDisallowUsage,
      handleClickSignOut,
    ],
  );

  const renderSideBarMobileButton = useCallback(
    () => (
      <button
        type="button"
        className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
        onClick={() => setSidebarOpen(true)}
      >
        <span className="sr-only">Open sidebar</span>
        <Menu
          className="h-7 w-7 rotate-180 rounded-full bg-gray-100 p-1 text-gray-800"
          aria-hidden="true"
        />
      </button>
    ),
    [setSidebarOpen],
  );

  return (
    <div className="flex h-screen">
      <AdvisorDashboardSideBar
        advisorSideBarMobile={renderAdvisorSideBarMobile()}
        isSidebarCollapsed={isSidebarCollapsed}
        setIsSidebarCollapsed={setIsSidebarCollapsed}
      >
        <AdvisorDashboardSideBarContent
          key={`mobile-sidebar-${activePage}`}
          activePage={activePage}
          onPageChange={handleOnPageChange}
          onNavigateToOnboarding={navigateToOnboarding}
          hasIncompleteOnboarding={hasIncompleteOnboarding}
          isDisabled={shouldDisallowUsage}
          handleClickSignOut={handleClickSignOut}
        />
      </AdvisorDashboardSideBar>
      <div className="flex flex-1 flex-col overflow-y-auto">
        <div className="sticky top-0 z-50 flex items-center gap-2 p-4 lg:hidden">
          {renderSideBarMobileButton()}
        </div>
        {isSidebarCollapsed && (
          <Button
            variant="outline"
            size="icon"
            className="border-primary-300 text-primary-dark hover:text-primary-hover absolute left-2 top-2 z-50 hidden h-7 w-7 lg:flex"
            id="open-sidebar"
            data-testid="open-sidebar"
            onClick={() => setIsSidebarCollapsed(false)}
          >
            <span className="sr-only">Open sidebar</span>
            <Menu className="h-4 w-4" />
          </Button>
        )}
        <main className="flex-1">
          <Routes>
            <Route
              path="/clients"
              element={<AdvisorDashboardClients />}
            />

            {/* <Route
              path="/opportunities"
              element={
                <AdvisorDashboardOpportunities
                  setShowInviteClientModal={setShowInviteClientModal}
                  setShowInviteClientModalWithPrepopulatedEmail={
                    setShowInviteClientModalWithPrepopulatedEmail
                  }
                />
              }
            /> */}

            <Route
              path="/settings"
              element={
                <AdvisorDashboardSettings
                  hasUnsavedChanges={hasUnsavedSettings}
                  setHasUnsavedChanges={setHasUnsavedSettings}
                  showUnsavedDialog={showUnsavedDialog}
                  setShowUnsavedDialog={setShowUnsavedDialog}
                />
              }
            />

            <Route
              path="/sponsorship"
              element={<AdvisorAccountSponsorship />}
            />

            <Route
              path="/seats"
              element={<AdvisorAccountSeats />}
            />

            <Route
              path="/integrations/*"
              element={<AdvisorIntegrations />}
            />

            <Route
              path="/account-action-required"
              element={<AdvisorAccountActionRequired />}
            />

            <Route
              path="/welcome"
              element={<AdvisorWelcome />}
            />
          </Routes>
        </main>
      </div>
      <InviteClientModal />
    </div>
  );
}
